// src/landing/RoutingLandingPage.jsx
import React, { useState, useEffect } from 'react';
import {
  Truck,
  BarChart3,
  Clock,
  Shield,
  CheckCircle,
  ArrowRight,
  MapPin,
  Package,
  Calendar,
  Users,
  Zap,
  Globe,
  AlertCircle,
  TrendingUp,
  Building
} from 'lucide-react';
import { ContactService } from '../shared/services/ContactService';

// ============================================================================
// INTERACTIVE DASHBOARD PREVIEW COMPONENTS
// ============================================================================

// Sample data for the preview
const SAMPLE_DATA = {
  summary: {
    totalLoads: 47,
    scheduled: 32,
    pending: 8,
    confirmed: 7,
    totalCases: 12450,
    totalPallets: 285
  },
  loads: [
    { loadId: 'WM-4521789', poCount: 3, status: 'Scheduled', carrier: 'SCHNEIDER', pickupDate: '01/28', destination: 'DC 6094', cases: 450, pallets: 12 },
    { loadId: 'WM-4521803', poCount: 2, status: 'Confirmed', carrier: 'JB HUNT', pickupDate: '01/29', destination: 'DC 7014', cases: 320, pallets: 8 },
    { loadId: 'WM-4521856', poCount: 4, status: 'Pending', carrier: '-', pickupDate: '-', destination: 'Store 1247', cases: 180, pallets: 5 },
    { loadId: 'WM-4521901', poCount: 1, status: 'Scheduled', carrier: 'SWIFT', pickupDate: '01/30', destination: 'DC 6023', cases: 520, pallets: 14 },
  ],
  mabdBreakdown: [
    { date: 'Jan 28', count: 12, status: 'on-track' },
    { date: 'Jan 29', count: 8, status: 'on-track' },
    { date: 'Jan 30', count: 15, status: 'warning' },
    { date: 'Jan 31', count: 7, status: 'on-track' },
    { date: 'Feb 1', count: 5, status: 'on-track' },
  ]
};

// Mini Summary Cards Component
const MiniSummaryCards = () => {
  const [animated, setAnimated] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setAnimated(true), 500);
    return () => clearTimeout(timer);
  }, []);

  const cards = [
    { label: 'Total Loads', value: SAMPLE_DATA.summary.totalLoads, icon: Truck, color: 'blue' },
    { label: 'Scheduled', value: SAMPLE_DATA.summary.scheduled, icon: CheckCircle, color: 'green' },
    { label: 'Pending', value: SAMPLE_DATA.summary.pending, icon: Clock, color: 'amber' },
    { label: 'Confirmed', value: SAMPLE_DATA.summary.confirmed, icon: Calendar, color: 'indigo' },
  ];

  return (
    <div className="grid grid-cols-4 gap-2">
      {cards.map((card, i) => (
        <div key={i} className="bg-white rounded-lg p-3 border border-gray-100 shadow-sm">
          <div className="flex items-center gap-2 mb-1">
            <card.icon className={`w-4 h-4 text-${card.color}-500`} />
            <span className="text-xs text-gray-500">{card.label}</span>
          </div>
          <div className={`text-xl font-bold text-gray-900 transition-all duration-1000 ${animated ? 'opacity-100' : 'opacity-0'}`}>
            {animated ? card.value : 0}
          </div>
        </div>
      ))}
    </div>
  );
};

// Mini Loads Table Component
const MiniLoadsTable = () => {
  const [hoveredRow, setHoveredRow] = useState(null);

  const getStatusColor = (status) => {
    switch(status) {
      case 'Scheduled': return 'bg-green-100 text-green-700';
      case 'Confirmed': return 'bg-blue-100 text-blue-700';
      case 'Pending': return 'bg-amber-100 text-amber-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div className="bg-gray-50 px-3 py-2 border-b border-gray-200">
        <span className="text-xs font-medium text-gray-600">Recent Loads</span>
      </div>
      <div className="divide-y divide-gray-100">
        {SAMPLE_DATA.loads.map((load, i) => (
          <div
            key={i}
            className={`px-3 py-2 text-xs transition-colors cursor-pointer ${hoveredRow === i ? 'bg-blue-50' : ''}`}
            onMouseEnter={() => setHoveredRow(i)}
            onMouseLeave={() => setHoveredRow(null)}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="font-medium text-gray-900">{load.loadId}</span>
                <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${getStatusColor(load.status)}`}>
                  {load.status}
                </span>
              </div>
              <div className="flex items-center gap-4 text-gray-500">
                <span>{load.carrier}</span>
                <span>{load.destination}</span>
                <span>{load.cases} cs</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Mini MABD Chart Component
const MiniMabdChart = () => {
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-3">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-medium text-gray-600">MABD Timeline</span>
        <span className="text-[10px] text-gray-400">Next 5 days</span>
      </div>
      <div className="flex items-end justify-between gap-2 h-16">
        {SAMPLE_DATA.mabdBreakdown.map((day, i) => (
          <div key={i} className="flex-1 flex flex-col items-center">
            <div
              className={`w-full rounded-t transition-all duration-500 ${
                day.status === 'warning' ? 'bg-amber-400' : 'bg-blue-400'
              }`}
              style={{ height: `${(day.count / 15) * 100}%`, minHeight: '8px' }}
            />
            <span className="text-[9px] text-gray-500 mt-1">{day.date.split(' ')[1]}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// Full Dashboard Preview Component
const DashboardPreview = () => {
  return (
    <div className="bg-gray-100 rounded-lg p-4 space-y-3">
      <MiniSummaryCards />
      <div className="grid grid-cols-3 gap-3">
        <div className="col-span-2">
          <MiniLoadsTable />
        </div>
        <div>
          <MiniMabdChart />
        </div>
      </div>
    </div>
  );
};

// Load Detail Preview Component
const LoadDetailPreview = () => {
  const load = SAMPLE_DATA.loads[0];

  return (
    <div className="bg-gray-100 rounded-lg p-4">
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {/* Header */}
        <div className="bg-blue-600 text-white px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Truck className="w-5 h-5" />
              <span className="font-semibold">{load.loadId}</span>
            </div>
            <span className="bg-white/20 px-2 py-0.5 rounded text-xs">Scheduled</span>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          {/* Info Grid */}
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div>
              <div className="text-gray-500 text-xs mb-1">Carrier</div>
              <div className="font-medium">{load.carrier}</div>
            </div>
            <div>
              <div className="text-gray-500 text-xs mb-1">Pickup Date</div>
              <div className="font-medium">{load.pickupDate}/2025</div>
            </div>
            <div>
              <div className="text-gray-500 text-xs mb-1">Destination</div>
              <div className="font-medium">{load.destination}</div>
            </div>
          </div>

          {/* Metrics */}
          <div className="flex items-center gap-6 py-3 border-t border-gray-100">
            <div className="flex items-center gap-2">
              <Package className="w-4 h-4 text-gray-400" />
              <span className="text-sm"><strong>{load.cases}</strong> cases</span>
            </div>
            <div className="flex items-center gap-2">
              <Building className="w-4 h-4 text-gray-400" />
              <span className="text-sm"><strong>{load.pallets}</strong> pallets</span>
            </div>
            <div className="flex items-center gap-2">
              <Package className="w-4 h-4 text-gray-400" />
              <span className="text-sm"><strong>{load.poCount}</strong> POs</span>
            </div>
          </div>

          {/* PO List Preview */}
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="text-xs text-gray-500 mb-2">Purchase Orders on this Load</div>
            <div className="space-y-1">
              {['PO-7821456', 'PO-7821457', 'PO-7821458'].map((po, i) => (
                <div key={i} className="flex items-center justify-between text-xs bg-white px-2 py-1.5 rounded">
                  <span className="font-medium text-gray-700">{po}</span>
                  <span className="text-gray-400">MABD: 01/30</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// MAIN LANDING PAGE COMPONENT
// ============================================================================

const RoutingLandingPage = ({ onSignIn }) => {
  const [showContactForm, setShowContactForm] = useState(false);
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    company: '',
    industry: '',
    vendorNumbers: '',
    message: ''
  });
  const [contactLoading, setContactLoading] = useState(false);
  const [contactError, setContactError] = useState('');
  const [contactSuccess, setContactSuccess] = useState(false);

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setContactLoading(true);
    setContactError('');
    setContactSuccess(false);

    try {
      // Validate form
      if (!contactForm.name.trim()) throw new Error('Name is required');
      if (!contactForm.email.trim()) throw new Error('Email is required');
      if (!contactForm.company.trim()) throw new Error('Company is required');

      const submissionData = {
        name: contactForm.name.trim(),
        email: contactForm.email.trim(),
        company: contactForm.company.trim(),
        industry: contactForm.industry.trim(),
        vendorNumbers: contactForm.vendorNumbers.trim(),
        message: contactForm.message.trim(),
        source: 'routing_landing_page',
        product: 'walmart_routing'
      };

      await ContactService.submitContactForm(submissionData);
      setContactSuccess(true);

      setTimeout(() => {
        setContactForm({ name: '', email: '', company: '', industry: '', vendorNumbers: '', message: '' });
        setShowContactForm(false);
        setContactSuccess(false);
      }, 3000);

    } catch (error) {
      console.error('Contact form error:', error);
      setContactError(error.message || 'Unable to submit. Please try again.');
    } finally {
      setContactLoading(false);
    }
  };

  const features = [
    {
      icon: <Truck className="w-6 h-6" />,
      title: 'Real-Time Load Tracking',
      description: 'Monitor all your Walmart and Sam\'s Club shipments in one dashboard. See status updates as they happen.'
    },
    {
      icon: <Calendar className="w-6 h-6" />,
      title: 'MABD Management',
      description: 'Never miss a Must Arrive By Date. Get alerts and visibility into upcoming deadlines across all orders.'
    },
    {
      icon: <BarChart3 className="w-6 h-6" />,
      title: 'Analytics & Insights',
      description: 'Understand your routing patterns, carrier performance, and identify opportunities to improve.'
    },
    {
      icon: <MapPin className="w-6 h-6" />,
      title: 'DC & Store Mapping',
      description: 'Visual maps of all destinations with transit times. Know exactly where your freight is going.'
    },
    {
      icon: <Package className="w-6 h-6" />,
      title: 'Order Consolidation',
      description: 'See which POs are on each load. Track cases, pallets, and cube utilization.'
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: 'Chargeback Defense',
      description: 'Document everything. Track tickets, notes, and issues to defend against OTIF chargebacks.'
    }
  ];

  const benefits = [
    'Reduce time spent manually checking TMS portals',
    'Catch routing issues before they become chargebacks',
    'One view of all vendor numbers across your organization',
    'Historical data for trend analysis and forecasting',
    'Team collaboration with notes and status tracking',
    'Works with your existing Walmart TMS exports'
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                <Truck className="w-6 h-6 text-white" />
              </div>
              <div>
                <span className="text-xl font-bold text-gray-900">Routing HQ</span>
                <span className="text-xs text-gray-500 block">for Walmart Suppliers</span>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <button
                onClick={() => setShowContactForm(true)}
                className="text-gray-600 hover:text-gray-900 font-medium"
              >
                Contact
              </button>
              {onSignIn && (
                <button
                  onClick={onSignIn}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors font-medium"
                >
                  Sign In
                </button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-900 via-blue-800 to-indigo-900 text-white">
        <div className="max-w-6xl mx-auto px-6 py-20">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-blue-700/50 text-blue-200 px-3 py-1 rounded-full text-sm mb-6">
                <Zap className="w-4 h-4" />
                Walmart & Sam's Club Routing Made Simple
              </div>

              <h1 className="text-4xl md:text-5xl font-bold mb-6 leading-tight">
                Stop Chasing Loads.<br />
                <span className="text-blue-300">Start Managing Them.</span>
              </h1>

              <p className="text-xl text-blue-100 mb-8">
                One dashboard for all your Walmart routing. Track loads, manage appointments,
                and prevent chargebacks — without logging into multiple portals.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={() => setShowContactForm(true)}
                  className="bg-white text-blue-900 px-6 py-3 rounded-lg font-semibold hover:bg-blue-50 transition-colors flex items-center justify-center gap-2"
                >
                  Start Free Trial
                  <ArrowRight className="w-5 h-5" />
                </button>
                <button
                  onClick={() => document.getElementById('features').scrollIntoView({ behavior: 'smooth' })}
                  className="border border-blue-400 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-800 transition-colors"
                >
                  See How It Works
                </button>
              </div>
            </div>

            <div className="relative">
              {/* Interactive Dashboard Preview */}
              <div className="bg-white rounded-xl shadow-2xl overflow-hidden">
                <div className="bg-gray-800 px-4 py-2 flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-red-400"></div>
                  <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
                  <div className="w-3 h-3 rounded-full bg-green-400"></div>
                  <span className="text-gray-400 text-xs ml-2">Routing HQ Dashboard</span>
                </div>
                <div className="p-2">
                  <DashboardPreview />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Bar */}
      <section className="bg-gray-50 py-6 border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex flex-wrap items-center justify-center gap-8 text-gray-500 text-sm">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-500" />
              <span>30-Day Free Trial</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-500" />
              <span>No Credit Card Required</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-500" />
              <span>Setup in Minutes</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-500" />
              <span>Works with Your TMS Exports</span>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Everything You Need to Manage Routing
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Built specifically for Walmart and Sam's Club suppliers who are tired of
              juggling spreadsheets and multiple portals.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-lg hover:border-blue-200 transition-all"
              >
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600 mb-4">
                  {feature.icon}
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {feature.title}
                </h3>
                <p className="text-gray-600">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Screenshots Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              See It In Action
            </h2>
            <p className="text-xl text-gray-600">
              A clean, intuitive interface designed for busy logistics teams.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Interactive Dashboard Preview */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
              <div className="bg-gray-800 px-4 py-2 flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-red-400"></div>
                <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
                <div className="w-3 h-3 rounded-full bg-green-400"></div>
                <span className="text-gray-400 text-sm ml-2">Dashboard Overview</span>
              </div>
              <DashboardPreview />
            </div>

            {/* Interactive Load Detail Preview */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
              <div className="bg-gray-800 px-4 py-2 flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-red-400"></div>
                <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
                <div className="w-3 h-3 rounded-full bg-green-400"></div>
                <span className="text-gray-400 text-sm ml-2">Load Details</span>
              </div>
              <LoadDetailPreview />
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Why Suppliers Choose Routing HQ
              </h2>
              <ul className="space-y-4">
                {benefits.map((benefit, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-blue-50 rounded-2xl p-8">
              <div className="flex items-center gap-3 mb-4">
                <Users className="w-8 h-8 text-blue-600" />
                <span className="text-lg font-semibold text-gray-900">Built for Teams</span>
              </div>
              <p className="text-gray-600 mb-6">
                Whether you're a single vendor number or managing dozens, Routing HQ scales with your business.
                Add team members, assign permissions, and collaborate on routing issues together.
              </p>
              <div className="flex items-center gap-2 text-blue-600 font-medium">
                <Globe className="w-5 h-5" />
                <span>Multi-vendor support included</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 bg-gray-900 text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Simple, Transparent Pricing
          </h2>
          <p className="text-xl text-gray-400 mb-12">
            No hidden fees. No long-term contracts. Cancel anytime.
          </p>

          <div className="bg-gray-800 rounded-2xl p-8 md:p-12 max-w-lg mx-auto">
            <div className="text-blue-400 font-medium mb-2">PER VENDOR NUMBER</div>
            <div className="flex items-baseline justify-center gap-2 mb-2">
              <span className="text-5xl md:text-6xl font-bold">$50</span>
              <span className="text-gray-400 text-xl">/month</span>
            </div>
            <p className="text-gray-400 mb-8">per Walmart vendor number</p>

            <div className="bg-green-900/30 border border-green-700 rounded-lg p-4 mb-8">
              <div className="flex items-center justify-center gap-2 text-green-400 font-semibold">
                <Clock className="w-5 h-5" />
                30-Day Free Trial
              </div>
              <p className="text-green-300 text-sm mt-1">No credit card required to start</p>
            </div>

            <ul className="text-left space-y-3 mb-8">
              {[
                'Unlimited users per vendor',
                'All dashboard features',
                'Data history & analytics',
                'Notes & ticket tracking',
                'Email support',
                'Regular updates'
              ].map((item, index) => (
                <li key={index} className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                  <span className="text-gray-300">{item}</span>
                </li>
              ))}
            </ul>

            <button
              onClick={() => setShowContactForm(true)}
              className="w-full bg-blue-600 text-white py-4 rounded-lg font-semibold hover:bg-blue-700 transition-colors text-lg"
            >
              Start Your Free Trial
            </button>
          </div>

          <p className="text-gray-500 mt-8 text-sm">
            Have multiple vendor numbers? <button onClick={() => setShowContactForm(true)} className="text-blue-400 hover:underline">Contact us</button> for volume pricing.
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-blue-600 text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Ready to Take Control of Your Routing?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Join suppliers who've simplified their Walmart routing operations.
          </p>
          <button
            onClick={() => setShowContactForm(true)}
            className="bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold hover:bg-blue-50 transition-colors text-lg inline-flex items-center gap-2"
          >
            Get Started Free
            <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-12">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                <Truck className="w-5 h-5 text-white" />
              </div>
              <span className="font-semibold text-white">Routing HQ</span>
            </div>

            <div className="flex items-center gap-6 text-sm">
              <button onClick={() => setShowContactForm(true)} className="hover:text-white transition-colors">
                Contact
              </button>
              <button className="hover:text-white transition-colors">Privacy</button>
              <button className="hover:text-white transition-colors">Terms</button>
            </div>

            <div className="text-sm">
              © {new Date().getFullYear()} ChainTo. All rights reserved.
            </div>
          </div>
        </div>
      </footer>

      {/* Contact Form Modal */}
      {showContactForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h3 className="text-xl font-bold text-gray-900">Start Your Free Trial</h3>
                <p className="text-gray-600 text-sm">We'll get you set up within 24 hours</p>
              </div>
              <button
                onClick={() => setShowContactForm(false)}
                className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
              >
                &times;
              </button>
            </div>

            {contactSuccess ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
                <h4 className="text-lg font-semibold text-gray-900 mb-2">Thank You!</h4>
                <p className="text-gray-600">We'll be in touch within 24 hours to get you started.</p>
              </div>
            ) : (
              <form onSubmit={handleContactSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                    <input
                      type="text"
                      value={contactForm.name}
                      onChange={(e) => setContactForm({...contactForm, name: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                    <input
                      type="email"
                      value={contactForm.email}
                      onChange={(e) => setContactForm({...contactForm, email: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Company *</label>
                  <input
                    type="text"
                    value={contactForm.company}
                    onChange={(e) => setContactForm({...contactForm, company: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Industry</label>
                    <select
                      value={contactForm.industry}
                      onChange={(e) => setContactForm({...contactForm, industry: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select...</option>
                      <option value="apparel">Apparel & Fashion</option>
                      <option value="food_beverage">Food & Beverage</option>
                      <option value="consumer_goods">Consumer Goods</option>
                      <option value="electronics">Electronics</option>
                      <option value="home_garden">Home & Garden</option>
                      <option value="health_beauty">Health & Beauty</option>
                      <option value="toys_games">Toys & Games</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1"># of Vendor Numbers</label>
                    <select
                      value={contactForm.vendorNumbers}
                      onChange={(e) => setContactForm({...contactForm, vendorNumbers: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select...</option>
                      <option value="1">1</option>
                      <option value="2-5">2-5</option>
                      <option value="6-10">6-10</option>
                      <option value="11-20">11-20</option>
                      <option value="20+">20+</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Message (optional)</label>
                  <textarea
                    value={contactForm.message}
                    onChange={(e) => setContactForm({...contactForm, message: e.target.value})}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Tell us about your routing challenges..."
                  />
                </div>

                {contactError && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <p className="text-red-800 text-sm">{contactError}</p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={contactLoading}
                  className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  {contactLoading ? 'Submitting...' : 'Start Free Trial'}
                </button>

                <p className="text-xs text-gray-500 text-center">
                  By submitting, you agree to our Terms of Service and Privacy Policy.
                </p>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default RoutingLandingPage;
